import {
  Body,
  Checkbox,
  Heading,
  Radio,
  Tooltip,
} from '@assembly-js/design-system';
import { createSelector } from '@reduxjs/toolkit';
import React, { ChangeEvent, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BankCardIcon, CreditCardIcon } from 'src/components/icons';
import { PortalConfigContext } from 'src/context';
import { ACHDisabledTooltip } from 'src/features/payments/components/ACHDisabledTooltip';
import { useHasACHCapability } from 'src/features/payments/hooks/useHasACHCapability';
import { usePaymentProcessingFee } from 'src/features/payments/hooks/usePaymentProcessingFee';
import { usePrimaryPaymentSource } from 'src/features/payments/hooks/usePaymentSources';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import { useAppSelector } from 'src/hooks/useStore';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { usePaymentMethods } from 'src/routes/payments/hooks/usePaymentMethods';
import { FeePayer, isValidFeePayer } from 'src/routes/payments/types';
import {
  Currencies,
  PaymentMethodType,
  PaymentPreferences,
} from 'src/services/api/invoicesApi';
import { RootState } from 'src/store';
import { GraySmall, white } from 'src/theme/colors';
import { arrayToMap } from 'src/utils/array';

const selector = createSelector(
  [
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.payments.accountInfo,
    (state: RootState) => state.activeCompany,
  ],
  (companies, accountInfo, activeCompany) => ({
    companies: arrayToMap(companies, 'id'),
    accountInfo,
    activeCompanyId: activeCompany.activeCompanyId ?? '',
  }),
);

type Props = {
  recipientId?: string;
  paymentPreferences: PaymentPreferences;
  onUpdatePaymentPreferences: (preferences: PaymentPreferences) => void;
  isOpen: boolean;
  onHandleClose: () => void;
  onHandleSuccess: () => void;
  positiveAction: boolean;
  totalAmount: number;
};

/**
 *
 * This component is used to manage the payment fees for manual charge invoices and subscriptions
 * It allows users to:
 * - View and select allowed payment methods
 * - Choose who will pay the associated transaction fee.
 *
 */

export const ManageManualPaymentSetting = ({
  isOpen,
  onHandleClose,
  onHandleSuccess,
  positiveAction,
  recipientId,
  paymentPreferences,
  onUpdatePaymentPreferences,
  totalAmount,
}: Props) => {
  const { companies, activeCompanyId } = useAppSelector(selector);
  const { t } = useTranslation();
  const company = companies.get(recipientId || '');

  const portalConfig = useContext(PortalConfigContext);
  const portalName = portalConfig.name;

  const companyId = company ? company.id : activeCompanyId;
  const defaultCurrency = useDefaultCurrency();
  const { shouldBlockBankPayments } = useHasACHCapability();

  const { defaultSource: defaultSourceId, sources } = usePaymentMethods({
    recipientId: recipientId || '',
    companyId,
  });

  const { selectedSource } = usePrimaryPaymentSource({
    defaultSourceId,
    sources,
  });

  const { creditCardFeeDetails, bankAccountFeeDetails } =
    usePaymentProcessingFee({
      amountDue: totalAmount * 100,
      source: selectedSource,
      preferences: paymentPreferences,
    });

  const availablePaymentMethods = useMemo(() => {
    return [
      defaultCurrency.toUpperCase() === Currencies.USD && {
        label: 'ACH',
        value: PaymentMethodType.BankAccount,
        icon: <BankCardIcon style={{ fontSize: 20, color: white }} />,
        feeLabel: bankAccountFeeDetails.feeLabel,
      },
      {
        label: 'Credit Card',
        value: PaymentMethodType.CreditCard,
        icon: <CreditCardIcon style={{ fontSize: 20, color: GraySmall }} />,
        feeLabel: creditCardFeeDetails.feeLabel,
      },
    ].filter(Boolean);
  }, [defaultCurrency, creditCardFeeDetails, bankAccountFeeDetails]);

  // When the connected account can't accept ACH, force the ACH preference off
  // so a document isn't saved offering an unsupported payment method. This can
  // leave no method selected (e.g. a bank-only document) — that's intended:
  // the user must pick credit card, which stays available in the modal.
  useEffect(() => {
    if (
      shouldBlockBankPayments &&
      paymentPreferences.paymentMethodTypes.includes(
        PaymentMethodType.BankAccount,
      )
    ) {
      onUpdatePaymentPreferences({
        ...paymentPreferences,
        paymentMethodTypes: paymentPreferences.paymentMethodTypes.filter(
          (method) => method !== PaymentMethodType.BankAccount,
        ),
        absorbTransactionFee: paymentPreferences.absorbTransactionFee.filter(
          (method) => method !== PaymentMethodType.BankAccount,
        ),
      });
    }
  }, [shouldBlockBankPayments, paymentPreferences, onUpdatePaymentPreferences]);

  /**
   * Method to handle the change of the allowed payment method
   */
  const handleAllowedPaymentMethodChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked, value } = event.target;
    const method = value as PaymentMethodType;

    // Prevent unchecking the last payment method
    if (paymentPreferences.paymentMethodTypes.length === 1 && !checked) return;

    // Prevent enabling bank/ACH when the connected account can't accept it
    if (
      method === PaymentMethodType.BankAccount &&
      shouldBlockBankPayments &&
      checked
    ) {
      return;
    }

    const updatedMethods = checked
      ? [...paymentPreferences.paymentMethodTypes, method]
      : paymentPreferences.paymentMethodTypes.filter(
          (currentMethod) => currentMethod !== method,
        );

    onUpdatePaymentPreferences({
      ...paymentPreferences,
      paymentMethodTypes: updatedMethods,
      absorbTransactionFee: paymentPreferences.absorbTransactionFee,
    });
  };

  /**
   * Handles the change in fee preference for a payment method.
   * - If the user selects 'Us', the payment method is added to the list of methods that will absorb the transaction fee.
   * - If the user selects 'Client', the payment method is removed from this list, meaning the client will pay the fee.
   */
  const handleFeesPreferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    paymentMethod: PaymentMethodType,
  ) => {
    if (isValidFeePayer(event.target.value)) {
      onUpdatePaymentPreferences({
        ...paymentPreferences,
        absorbTransactionFee:
          event.target.value === FeePayer.Client
            ? paymentPreferences.absorbTransactionFee.filter(
                (method) => method !== paymentMethod,
              )
            : [...paymentPreferences.absorbTransactionFee, paymentMethod],
      });
    }
  };

  const feeLabelRenderer = ({
    feeLabel,
    paymentMethod,
  }: {
    feeLabel: string;
    paymentMethod: PaymentMethodType;
  }): string => {
    // check if fee is payed by internal user
    return paymentPreferences.absorbTransactionFee.includes(paymentMethod)
      ? t(($) => $.page_billing.fee_payer.iu, {
          portalName: portalName,
        })
      : t(($) => $.page_billing.fee_payer.cu, {
          feeLabel: feeLabel,
        });
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={onHandleClose}
      positiveAction={positiveAction}
      onSuccess={onHandleSuccess}
      successButtonLabel="Save"
      title="Manage payment preferences"
    >
      <div>
        {availablePaymentMethods.map((paymentMethod) => {
          const isChecked = paymentPreferences.paymentMethodTypes.includes(
            paymentMethod.value,
          );
          const isLastMethod =
            paymentPreferences.paymentMethodTypes.length === 1 && isChecked;
          const isBankBlocked =
            paymentMethod.value === PaymentMethodType.BankAccount &&
            shouldBlockBankPayments;
          // Show ACH as unchecked + disabled when the capability is missing.
          // (The effect above strips it from the saved preferences too.)
          const displayChecked = isBankBlocked ? false : isChecked;
          const isDisabled = isLastMethod || isBankBlocked;
          const checkbox = (
            <div>
              <Checkbox
                label=""
                value={paymentMethod.value}
                checked={displayChecked}
                disabled={isDisabled}
                onChange={handleAllowedPaymentMethodChange}
              />
            </div>
          );

          return (
            <div
              key={`${paymentMethod.label}-${paymentMethod.value}`}
              className="mb-6"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {paymentMethod.icon}
                  <Body className="text-[14px] font-normal text-black-heading">
                    {paymentMethod.label}
                  </Body>
                </div>

                {isBankBlocked ? (
                  <ACHDisabledTooltip>{checkbox}</ACHDisabledTooltip>
                ) : (
                  <Tooltip
                    disabled={!isLastMethod}
                    content="At least one payment method must be set."
                  >
                    {checkbox}
                  </Tooltip>
                )}
              </div>
              {displayChecked && (
                <div className="flex justify-between border border-solid border-non-hover-border rounded-4 p-3 ">
                  <div>
                    <Heading className="text-[14px] text-dark-black">
                      Decide who pays the fee
                    </Heading>
                    <Body className="text-[12px] text-gray-small">
                      {feeLabelRenderer({
                        feeLabel: paymentMethod.feeLabel,
                        paymentMethod: paymentMethod.value,
                      })}
                    </Body>
                  </div>
                  <div>
                    <label className="flex gap-2 items-center justify-end mb-2">
                      <Body className="text-[14px] text-dark-black">Us</Body>
                      <div className="flex items-center justify-center h-5 w-5">
                        <Radio
                          label=""
                          value={FeePayer.Internal}
                          checked={paymentPreferences.absorbTransactionFee.includes(
                            paymentMethod.value,
                          )}
                          onChange={(e) =>
                            handleFeesPreferenceChange(e, paymentMethod.value)
                          }
                          classes={{
                            root: '[&>div]:gap-0 w-5 h-5',
                          }}
                        />
                      </div>
                    </label>
                    <label className="flex gap-2 items-center justify-end">
                      <Body className="text-[14px] text-dark-black">
                        {` ${t(($) => $.client, { count: 1 })}`}
                      </Body>
                      <div className="flex items-center justify-center h-5 w-5">
                        <Radio
                          label=""
                          value={FeePayer.Client}
                          checked={
                            !paymentPreferences.absorbTransactionFee.includes(
                              paymentMethod.value,
                            )
                          }
                          onChange={(e) =>
                            handleFeesPreferenceChange(e, paymentMethod.value)
                          }
                          color="primary"
                          classes={{
                            root: '[&>div]:gap-0 w-5 h-5',
                          }}
                        />
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModalWrapper>
  );
};
